import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, Bike, Sparkles, ArrowDown } from "lucide-react";
import { waLink } from "@/lib/api";
import { scrollTo } from "@/components/Nav";

const HERO_IMG =
  "https://images.unsplash.com/photo-1659874817127-85c7aed28a02?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";

const lines = [
  { text: "Explore Puri's", cls: "text-sand" },
  { text: "Sacred Coasts", cls: "text-gold italic" },
  { text: "& Beyond", cls: "text-sand" },
];

export default function Hero({ onOpenAi }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-end overflow-hidden bg-ink" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <img src={HERO_IMG} alt="Puri beach at golden hour" className="w-full h-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 hero-vignette" />

      <motion.div style={{ y: textY, opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 pt-40 w-full">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.7 }}
          className="font-mono text-xs uppercase tracking-[0.3em] text-gold mb-6"
          data-testid="hero-eyebrow"
        >
          Random Rider · Puri, Odisha
        </motion.p>

        <h1 className="font-serif font-black tracking-tight leading-[1.02] text-5xl sm:text-6xl lg:text-8xl" data-testid="hero-headline">
          {lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, delay: 0.35 + i * 0.18, ease: [0.22, 1, 0.36, 1] }}
                className={`block ${line.cls}`}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-6 max-w-xl text-base sm:text-lg text-sand/85 leading-relaxed"
          data-testid="hero-subtitle"
        >
          Scooter & bike rentals, Puri local sightseeing and all-Odisha tour packages —
          with local drivers who know every gali, ghat and golden-hour spot.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.25, duration: 0.8 }}
          className="mt-5 inline-flex items-center gap-2 bg-terra/90 backdrop-blur text-sand text-xs sm:text-sm font-semibold px-4 py-2 rounded-full"
          data-testid="hero-rate-pill"
        >
          <Sparkles size={14} /> Outstation cabs over 50 km at just ₹20/km — car, fuel & driver included
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4, duration: 0.8 }}
          className="mt-8 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-explore-rentals-button"
            onClick={() => scrollTo("#rentals")}
            className="flex items-center gap-2 bg-terra text-sand font-semibold px-7 py-3.5 rounded-full hover:bg-gold hover:text-ink hover:scale-[1.04] transition-all duration-300"
          >
            <Bike size={18} /> Explore Rentals
          </button>
          <a
            href={waLink("Hi Random Rider! I want to book a tour / rental in Puri.")}
            target="_blank"
            rel="noreferrer"
            data-testid="hero-whatsapp-button"
            className="flex items-center gap-2 bg-[#25D366] text-white font-semibold px-7 py-3.5 rounded-full hover:brightness-110 hover:scale-[1.04] transition-all duration-300"
          >
            <MessageCircle size={18} /> Chat on WhatsApp
          </a>
          <button
            data-testid="hero-ai-planner-button"
            onClick={onOpenAi}
            className="flex items-center gap-2 border border-sand/40 text-sand font-semibold px-7 py-3.5 rounded-full hover:bg-sand hover:text-ink transition-all duration-300"
          >
            <Sparkles size={18} /> AI Trip Assistant
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-sand/60"
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={20} />
        </motion.div>
      </motion.div>
    </section>
  );
}
